import React, { useCallback, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";

import {
     buildRow,
     isAccredited,
     isEmptyShell,
     readHeaderColumns,
     rowProblem,
     SERVICE_ADVISORS_SHEET,
} from "../../optimumFrictionTrainingRow";

// The table scrolls inside itself with its header pinned, rather than running the page on down the
// screen. The file carries a few hundred advisors.
const GRID_MAX_HEIGHT = "58vh";

// Shown before a file is picked, so an administrator knows what pressing import will do before it
// happens rather than after. These must stay in step with the rules the backend applies.
const UPLOAD_RULES = [
     "The file must be a plain .xlsx with no password on it. Open the Academy file, enter its password once, and save a copy without one.",
     "Only the Service Advisors sheet is read. Service Managers are ignored, because a manager never receives a card.",
     "An advisor is matched on their email address, so a row with no email is left out.",
     "An advisor nobody on Equipe holds is created at the dealer code on the row and sent an invitation.",
     "Only a Brands & Products status of Successful grants accreditation. Every other value is reported and left alone.",
     "Accreditation is only ever granted here. Taking it away is an administrator's job, not this file's.",
     "Running the same file twice grants nothing the second time.",
];

export default function OptimumFrictionAccreditationImport(props) {
     const { isImporting, onImport } = props;

     const [fileName, setFileName] = useState("");
     const [readError, setReadError] = useState("");
     const [workbook, setWorkbook] = useState(null);
     const [sheetNames, setSheetNames] = useState([]);
     const [sheetName, setSheetName] = useState("");
     const [rows, setRows] = useState([]);
     const [skippedCount, setSkippedCount] = useState(0);

     const setRowField = useCallback((rowIndex, field, value) => {
          setRows((previous) =>
               previous.map((row, index) =>
                    index === rowIndex ? { ...row, [field]: value } : row,
               ),
          );
     }, []);

     // An added row starts empty. Nothing is carried down from the row above, because a value
     // that arrived on its own is a value nobody checked. The row number continues the file's, so
     // a result still points at something an administrator can find.
     const addRow = useCallback(() => {
          setRows((previous) => [
               ...previous,
               {
                    rowNumber:
                         previous.reduce(
                              (highest, row) =>
                                   Math.max(highest, row.rowNumber || 0),
                              0,
                         ) + 1,
                    email: "",
                    username: "",
                    firstName: "",
                    lastName: "",
                    dealerCode: "",
                    dealerName: "",
                    brandsAndProductsStatus: "",
               },
          ]);
     }, []);

     const removeRow = useCallback((rowIndex) => {
          setRows((previous) =>
               previous.filter((row, index) => index !== rowIndex),
          );
     }, []);

     // Reads one sheet. The header is found wherever it sits rather than assumed on row one,
     // because the Academy's export opens with a title block above its own column names.
     const readSheet = useCallback((book, name) => {
          const sheet = book.Sheets[name];
          const grid = XLSX.utils.sheet_to_json(sheet, {
               header: 1,
               defval: "",
               blankrows: false,
          });

          let columns = null;
          let skipped = 0;
          const parsed = [];

          grid.forEach((cells, index) => {
               const headerColumns = readHeaderColumns(cells);

               if (headerColumns) {
                    columns = headerColumns;
                    return;
               }

               if (!columns) {
                    return;
               }

               const row = buildRow(cells, columns, index + 1);

               if (isEmptyShell(row)) {
                    skipped += 1;
                    return;
               }

               parsed.push(row);
          });

          setSheetName(name);
          setRows(parsed);
          setSkippedCount(skipped);

          if (!columns) {
               setReadError(
                    "No column headings were found on that sheet. Check it is the Service Advisors sheet.",
               );
          }
     }, []);

     const readFile = (event) => {
          const file = event.target.files && event.target.files[0];

          setReadError("");
          setRows([]);
          setWorkbook(null);
          setSheetNames([]);
          setSkippedCount(0);
          setFileName(file ? file.name : "");

          if (!file) {
               return;
          }

          const reader = new FileReader();

          reader.onerror = () =>
               setReadError(
                    "That file could not be read. Try saving it again as .xlsx.",
               );

          reader.onload = () => {
               try {
                    const book = XLSX.read(reader.result, {
                         type: "array",
                         cellDates: true,
                    });

                    if (!book.SheetNames.length) {
                         setReadError("That file has no sheets.");
                         return;
                    }

                    setWorkbook(book);
                    setSheetNames(book.SheetNames);

                    // The advisors sheet is opened by name, so a workbook whose first tab is the
                    // managers does not silently load the wrong people.
                    const advisors = book.SheetNames.find(
                         (name) =>
                              name.trim().toLowerCase() ===
                              SERVICE_ADVISORS_SHEET.toLowerCase(),
                    );

                    readSheet(book, advisors || book.SheetNames[0]);
               } catch (error) {
                    setReadError(
                         "That file could not be read as a spreadsheet. If it is password protected, save a copy without the password first.",
                    );
               }
          };

          reader.readAsArrayBuffer(file);
     };

     // The row number and the problem are computed from the rows rather than stored, so neither can
     // disagree with what is on screen after an edit.
     const gridRows = useMemo(
          () =>
               rows.map((row, index) => ({
                    ...row,
                    rowIndex: index,
                    problem: rowProblem(row),
                    willGrant: isAccredited(row) ? "Yes" : "No",
               })),
          [rows],
     );

     const loadable = useMemo(
          () => gridRows.filter((row) => !row.problem),
          [gridRows],
     );

     const grantCount = useMemo(
          () => loadable.filter((row) => row.willGrant === "Yes").length,
          [loadable],
     );

     // Built once. Rebuilding a cell on every render hands the input a new identity per keystroke,
     // which loses the caret.
     const cells = useMemo(() => {
          const textCell = (field) => (cellProps) => (
               <td>
                    <Input
                         style={{ width: "100%" }}
                         value={cellProps.dataItem[field] || ""}
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   field,
                                   event.value,
                              )
                         }
                    />
               </td>
          );

          return {
               email: textCell("email"),
               firstName: textCell("firstName"),
               lastName: textCell("lastName"),
               dealerCode: textCell("dealerCode"),
               brandsAndProductsStatus: textCell("brandsAndProductsStatus"),
               remove: (cellProps) => (
                    <td>
                         <Tooltip title="Remove row">
                              <IconButton
                                   aria-label="Remove row"
                                   onClick={() =>
                                        removeRow(cellProps.dataItem.rowIndex)
                                   }
                              >
                                   <DeleteIcon color="error" />
                              </IconButton>
                         </Tooltip>
                    </td>
               ),
          };
     }, [removeRow, setRowField]);

     // A row that still has a problem is tinted whole, so it reads as one thing to fix rather than
     // a sentence to hunt for at the end of a wide table.
     const rowRender = (element, rowProps) => {
          if (!rowProps.dataItem.problem) {
               return element;
          }

          return React.cloneElement(
               element,
               {
                    ...element.props,
                    style: { backgroundColor: "#fdecea" },
               },
               element.props.children,
          );
     };

     return (
          <Box>
               {!fileName && (
                    <Box component="section" sx={{ mb: 3 }}>
                         <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "bold", mb: 1 }}
                         >
                              What this upload does
                         </Typography>
                         <Box component="ul" sx={{ m: 0, pl: 3 }}>
                              {UPLOAD_RULES.map((rule) => (
                                   <Typography
                                        component="li"
                                        variant="body2"
                                        key={rule}
                                        sx={{ mb: 0.5 }}
                                   >
                                        {rule}
                                   </Typography>
                              ))}
                         </Box>
                    </Box>
               )}

               <input type="file" accept=".xlsx,.xls" onChange={readFile} />

               {sheetNames.length > 0 && (
                    <Box
                         sx={{
                              pt: 2,
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-end",
                              gap: 2,
                         }}
                    >
                         <DropDownList
                              style={{ width: "280px" }}
                              label="Sheet"
                              data={sheetNames}
                              value={sheetName}
                              onChange={(event) =>
                                   readSheet(workbook, event.value)
                              }
                         />
                    </Box>
               )}

               {fileName && (
                    <Typography
                         variant="caption"
                         sx={{ display: "block", pt: 1 }}
                    >
                         {fileName}
                    </Typography>
               )}

               {readError && (
                    <Typography variant="body2" color="error" sx={{ pt: 1 }}>
                         {readError}
                    </Typography>
               )}

               {gridRows.length > 0 && (
                    <Box sx={{ pt: 3 }}>
                         <Typography variant="body2" sx={{ mb: 1 }}>
                              {loadable.length} of {gridRows.length} rows can be
                              checked, and {grantCount} of them would grant
                              accreditation. Anyone new to Equipe is created and
                              invited, whatever their status says.
                              {skippedCount > 0
                                   ? ` ${skippedCount} empty rows were left out of the file.`
                                   : ""}
                         </Typography>

                         <Grid
                              data={gridRows}
                              className="sba-area-data-grid"
                              rowRender={rowRender}
                              style={{ maxHeight: GRID_MAX_HEIGHT }}
                         >
                              <GridColumn
                                   field="rowNumber"
                                   title="Row"
                                   width="80px"
                              />
                              <GridColumn
                                   field="firstName"
                                   title="First"
                                   width="150px"
                                   cells={{ data: cells.firstName }}
                              />
                              <GridColumn
                                   field="lastName"
                                   title="Last"
                                   width="150px"
                                   cells={{ data: cells.lastName }}
                              />
                              <GridColumn
                                   field="email"
                                   title="Email"
                                   width="260px"
                                   cells={{ data: cells.email }}
                              />
                              <GridColumn
                                   field="dealerCode"
                                   title="Dealer Code"
                                   width="140px"
                                   cells={{ data: cells.dealerCode }}
                              />
                              <GridColumn
                                   field="brandsAndProductsStatus"
                                   title="Brands & Products"
                                   width="200px"
                                   cells={{
                                        data: cells.brandsAndProductsStatus,
                                   }}
                              />
                              <GridColumn
                                   field="willGrant"
                                   title="Grants"
                                   width="100px"
                              />
                              <GridColumn
                                   title=""
                                   width="70px"
                                   cells={{ data: cells.remove }}
                              />
                              <GridColumn
                                   field="problem"
                                   title="Problem"
                                   width="380px"
                              />
                         </Grid>

                         <Box
                              sx={{
                                   mt: 1,
                                   display: "flex",
                                   justifyContent: "flex-end",
                              }}
                         >
                              <Tooltip title="Add a row">
                                   <IconButton
                                        aria-label="Add a row"
                                        onClick={addRow}
                                        sx={{
                                             backgroundColor: "#1976d2",
                                             color: "#fff",
                                             "&:hover": {
                                                  backgroundColor: "#1565c0",
                                             },
                                        }}
                                   >
                                        <AddIcon />
                                   </IconButton>
                              </Tooltip>
                         </Box>

                         <Box sx={{ pt: 3 }}>
                              <Button
                                   type="button"
                                   themeColor="primary"
                                   disabled={isImporting || !loadable.length}
                                   onClick={() => onImport(loadable, fileName)}
                              >
                                   {isImporting
                                        ? "Importing..."
                                        : `Import ${loadable.length} rows`}
                              </Button>
                         </Box>
                    </Box>
               )}
          </Box>
     );
}
